import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { ToolService } from '../../../lib/toolAccess';

type AccessSetting = {
  service: ToolService;
  label: string;
  enabled: boolean;
  free_daily_uses: number;
};

type AccessUser = {
  id: string;
  username: string;
  active: boolean;
  expires_at: string | null;
  note: string | null;
  created_at: string;
  active_sessions: number;
};

type SessionCountRow = {
  user_id: string;
  active_sessions: number | string;
};

const serviceOrder: ToolService[] = ['simulator', 'plc', 'faults', 'lighting'];
const fallbackLabels: Record<ToolService, string> = {
  simulator: 'محاكي الدوائر الكهربائية',
  plc: 'محاكي PLC',
  faults: 'فحص أعطال الكهرباء',
  lighting: 'حساب الإضاءة المناسبة',
};

function randomText(length: number, alphabet: string) {
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join('');
}

function formatDate(value: string | null) {
  if (!value) return 'بدون انتهاء';
  try {
    return new Intl.DateTimeFormat('ar-IQ', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
  } catch {
    return value;
  }
}

export default function AccessPanel() {
  const [settings, setSettings] = useState<AccessSetting[]>([]);
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [setupMissing, setSetupMissing] = useState(false);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [days, setDays] = useState(30);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setMessage('');
    const [settingsResult, usersResult, sessionsResult] = await Promise.all([
      supabase.from('tool_access_settings').select('service,label,enabled,free_daily_uses'),
      supabase.from('tool_access_users').select('id,username,active,expires_at,note,created_at').order('created_at', { ascending: false }),
      supabase.rpc('admin_tool_session_counts'),
    ]);

    if (settingsResult.error || usersResult.error) {
      setSetupMissing(true);
      setMessage('نفّذ ملف supabase/access_control_setup.sql مرة واحدة داخل Supabase حتى يشتغل نظام الحسابات.');
    } else {
      setSetupMissing(false);
      const rows = (settingsResult.data ?? []) as AccessSetting[];
      setSettings(
        serviceOrder.map((service) => rows.find((row) => row.service === service) ?? {
          service,
          label: fallbackLabels[service],
          enabled: true,
          free_daily_uses: 2,
        }),
      );

      const sessionRows = (sessionsResult.data ?? []) as SessionCountRow[];
      const sessionMap = new Map(
        sessionRows.map((row) => [row.user_id, Number(row.active_sessions) || 0]),
      );
      setUsers(
        ((usersResult.data ?? []) as Omit<AccessUser, 'active_sessions'>[]).map((user) => ({
          ...user,
          active_sessions: sessionMap.get(user.id) ?? 0,
        })),
      );

      if (sessionsResult.error) {
        setMessage('لتفعيل عدّ الجلسات وحدّ الجلستين، نفّذ ملف supabase/access_control_two_sessions_update.sql مرة واحدة داخل Supabase.');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const activeUsers = useMemo(
    () => users.filter((user) => user.active && (!user.expires_at || new Date(user.expires_at).getTime() > Date.now())).length,
    [users],
  );

  const activeSessions = useMemo(
    () => users.reduce((total, user) => total + user.active_sessions, 0),
    [users],
  );

  async function saveSettings() {
    if (!supabase) return;
    setSaving(true);
    setMessage('');
    const payload = settings.map((item) => ({
      service: item.service,
      label: item.label || fallbackLabels[item.service],
      enabled: item.enabled,
      free_daily_uses: Math.max(0, Math.min(50, Number(item.free_daily_uses) || 0)),
      updated_at: new Date().toISOString(),
    }));
    const { error } = await supabase.from('tool_access_settings').upsert(payload, { onConflict: 'service' });
    setMessage(error ? `تعذر الحفظ: ${error.message}` : 'تم حفظ عدد مرات التجربة المجانية.');
    setSaving(false);
  }

  async function createUser(event: FormEvent) {
    event.preventDefault();
    if (!supabase || !username.trim() || !password || days < 1) return;
    setSaving(true);
    setMessage('');
    const expiresAt = new Date(Date.now() + days * 86400000).toISOString();
    const { data, error } = await supabase.rpc('admin_tool_create_user', {
      p_username: username.trim(),
      p_password: password,
      p_expires_at: expiresAt,
      p_note: note.trim() || null,
    });
    if (error) {
      setMessage(`تعذر إنشاء الحساب: ${error.message}`);
    } else {
      const row = Array.isArray(data) ? data[0] : data;
      if (row && typeof row === 'object' && 'ok' in row && row.ok === false) {
        setMessage(String(row.message ?? 'تعذر إنشاء الحساب.'));
      } else {
        setMessage(`تم إنشاء الحساب: ${username.trim()}`);
        setUsername('');
        setPassword('');
        setNote('');
        await load();
      }
    }
    setSaving(false);
  }

  async function setUserActive(user: AccessUser, active: boolean) {
    if (!supabase) return;
    const { error } = await supabase.from('tool_access_users').update({ active }).eq('id', user.id);
    if (!error) setUsers((current) => current.map((item) => item.id === user.id ? { ...item, active } : item));
    else setMessage(`تعذر تعديل الحساب: ${error.message}`);
  }

  async function extendUser(user: AccessUser, extraDays: number) {
    if (!supabase) return;
    const base = user.expires_at && new Date(user.expires_at).getTime() > Date.now()
      ? new Date(user.expires_at).getTime()
      : Date.now();
    const expiresAt = new Date(base + extraDays * 86400000).toISOString();
    const { error } = await supabase.from('tool_access_users').update({ expires_at: expiresAt, active: true }).eq('id', user.id);
    if (!error) setUsers((current) => current.map((item) => item.id === user.id ? { ...item, expires_at: expiresAt, active: true } : item));
    else setMessage(`تعذر تمديد الحساب: ${error.message}`);
  }

  async function resetPassword(user: AccessUser) {
    if (!supabase) return;
    const next = window.prompt(`اكتب كلمة المرور الجديدة للحساب ${user.username}`);
    if (!next || next.length < 4) return;
    const { data, error } = await supabase.rpc('admin_tool_set_password', { p_user_id: user.id, p_password: next });
    const row = Array.isArray(data) ? data[0] : data;
    if (error || (row && typeof row === 'object' && 'ok' in row && row.ok === false)) {
      setMessage(error?.message ?? String(row?.message ?? 'تعذر تغيير كلمة المرور.'));
    } else {
      setMessage(`تم تغيير كلمة مرور ${user.username}.`);
    }
  }

  async function deleteUser(user: AccessUser) {
    if (!supabase || !window.confirm(`حذف الحساب ${user.username} نهائياً؟`)) return;
    const { error } = await supabase.from('tool_access_users').delete().eq('id', user.id);
    if (!error) setUsers((current) => current.filter((item) => item.id !== user.id));
    else setMessage(`تعذر حذف الحساب: ${error.message}`);
  }

  if (loading) return <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-gray-300">جاري تحميل نظام الحسابات...</div>;

  if (setupMissing) {
    return (
      <div className="rounded-2xl border border-[#F5A623]/30 bg-[#F5A623]/5 p-6">
        <h2 className="text-xl font-bold text-[#F5A623]">تفعيل نظام الحسابات</h2>
        <p className="mt-3 text-sm leading-8 text-gray-300">{message}</p>
        <p className="mt-2 text-xs text-gray-500" dir="ltr">Supabase → SQL Editor → access_control_setup.sql → Run</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5"><p className="text-xs text-gray-400">كل الحسابات</p><p className="mt-2 text-3xl font-black text-[#F5A623]">{users.length}</p></div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5"><p className="text-xs text-gray-400">الحسابات الفعالة</p><p className="mt-2 text-3xl font-black text-green-400">{activeUsers}</p></div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5"><p className="text-xs text-gray-400">الجلسات المسجلة</p><p className="mt-2 text-3xl font-black text-sky-300">{activeSessions}</p><p className="mt-1 text-[11px] text-gray-500">الحد: جلستان لكل حساب</p></div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5"><p className="text-xs text-gray-400">الخدمات المحمية</p><p className="mt-2 text-3xl font-black">{settings.filter((item) => item.enabled).length}</p></div>
      </div>

      <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h2 className="text-lg font-bold">التجربة المجانية اليومية</h2><p className="mt-1 text-xs text-gray-400">حدد عدد مرات الدخول المجانية لكل خدمة في اليوم. صفر يعني يطلب حساب من أول مرة.</p></div>
          <button onClick={saveSettings} disabled={saving} className="rounded-xl bg-[#F5A623] px-5 py-2.5 text-sm font-bold text-black disabled:opacity-60">حفظ الإعدادات</button>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {settings.map((item) => (
            <div key={item.service} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex items-center justify-between gap-3">
                <div><p className="font-bold">{item.label}</p><p className="mt-1 text-[11px] text-gray-500" dir="ltr">{item.service}</p></div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={item.enabled} onChange={(e) => setSettings((current) => current.map((row) => row.service === item.service ? { ...row, enabled: e.target.checked } : row))} className="h-5 w-5 accent-[#F5A623]" /> تفعيل الحماية</label>
              </div>
              <div className="mt-4 flex items-center gap-3"><label className="text-sm text-gray-300">مرات مجانية/اليوم</label><input type="number" min={0} max={50} value={item.free_daily_uses} onChange={(e) => setSettings((current) => current.map((row) => row.service === item.service ? { ...row, free_daily_uses: Number(e.target.value) } : row))} className="admin-input max-w-28" /></div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
        <h2 className="text-lg font-bold">إنشاء يوزر وباسورد</h2>
        <p className="mt-1 text-xs text-gray-400">هذا الحساب يفتح الخدمات الأربع كلها إلى تاريخ انتهاء الاشتراك.</p>
        <form onSubmit={createUser} className="mt-5 grid gap-4 md:grid-cols-2">
          <div><label className="mb-2 block text-sm text-gray-300">اسم المستخدم</label><div className="flex gap-2"><input value={username} onChange={(e) => setUsername(e.target.value)} className="admin-input" dir="ltr" required /><button type="button" onClick={() => setUsername(`user${randomText(5, '23456789')}`)} className="rounded-xl border border-white/10 px-3 text-xs">توليد</button></div></div>
          <div><label className="mb-2 block text-sm text-gray-300">كلمة المرور</label><div className="flex gap-2"><input value={password} onChange={(e) => setPassword(e.target.value)} className="admin-input" dir="ltr" required minLength={4} /><button type="button" onClick={() => setPassword(randomText(10, 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'))} className="rounded-xl border border-white/10 px-3 text-xs">توليد</button></div></div>
          <div><label className="mb-2 block text-sm text-gray-300">مدة الاشتراك (يوم)</label><input type="number" min={1} max={3650} value={days} onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 1))} className="admin-input" /></div>
          <div><label className="mb-2 block text-sm text-gray-300">ملاحظة داخلية (اختياري)</label><input value={note} onChange={(e) => setNote(e.target.value)} className="admin-input" /></div>
          <button disabled={saving} className="md:col-span-2 rounded-xl bg-[#F5A623] py-3 font-bold text-black disabled:opacity-60">إنشاء الحساب</button>
        </form>
      </section>

      {message && <p className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-gray-200">{message}</p>}

      <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">الحسابات</h2>
            <p className="mt-1 text-xs text-gray-400">كل حساب مسموح له بجلستين فقط. محاولة الدخول الثالثة تُرفض تلقائياً.</p>
          </div>
          <button onClick={() => void load()} className="rounded-xl border border-white/10 px-4 py-2 text-xs text-gray-200 hover:bg-white/5">تحديث الجلسات</button>
        </div>
        {!users.length ? <p className="mt-4 text-sm text-gray-400">ماكو حسابات لحد الآن.</p> : (
          <div className="mt-4 space-y-3">
            {users.map((user) => {
              const expired = Boolean(user.expires_at && new Date(user.expires_at).getTime() <= Date.now());
              return (
                <div key={user.id} className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div><p className="font-bold text-lg" dir="ltr">{user.username}</p><p className="mt-1 text-xs text-gray-400">الانتهاء: {formatDate(user.expires_at)}</p>{user.note && <p className="mt-1 text-xs text-gray-500">{user.note}</p>}</div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${user.active_sessions >= 2 ? 'bg-[#F5A623]/15 text-[#F5A623]' : 'bg-sky-500/15 text-sky-300'}`}>الجلسات: {user.active_sessions} / 2</span>
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${!user.active || expired ? 'bg-red-500/15 text-red-300' : 'bg-green-500/15 text-green-300'}`}>{!user.active ? 'موقوف' : expired ? 'منتهي' : 'فعال'}</span>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button onClick={() => setUserActive(user, !user.active)} className="rounded-lg border border-white/10 px-3 py-2 text-xs">{user.active ? 'إيقاف' : 'تفعيل'}</button>
                    <button onClick={() => extendUser(user, 30)} className="rounded-lg border border-white/10 px-3 py-2 text-xs">+30 يوم</button>
                    <button onClick={() => extendUser(user, 365)} className="rounded-lg border border-white/10 px-3 py-2 text-xs">+سنة</button>
                    <button onClick={() => resetPassword(user)} className="rounded-lg border border-white/10 px-3 py-2 text-xs">تغيير الباسورد</button>
                    <button onClick={() => deleteUser(user)} className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-300">حذف</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-xs leading-7 text-gray-500">
        التجربة المجانية تُحسب لكل متصفح/جهاز. المستخدم الذي يمسح بيانات المتصفح يمكنه الحصول على معرّف جديد؛ الحسابات المدفوعة نفسها تُتحقق من السيرفر ويمكن إيقافها أو تحديد مدة صلاحيتها من هذه الصفحة.
      </div>
    </div>
  );
}
